/**
 * B2BCartContext
 *
 * Lightweight client-side cart using React context + useReducer.
 * No persistence to DB yet — Phase 4.2 will add order submission.
 *
 * Shape:
 *   CartItem  { product: CatalogProduct; quantity: number }
 *   CartState { items: CartItem[] }
 *
 * Actions:
 *   ADD_ITEM     — adds qty to an existing item or inserts a new one
 *   SET_QUANTITY — sets the exact quantity (0 removes the item)
 *   REMOVE_ITEM  — removes by product id
 *   CLEAR        — empties the cart
 */

import {
  createContext,
  useContext,
  useReducer,
  useCallback,
  ReactNode,
} from 'react';

// ─── Types ────────────────────────────────────────────────────────────────────

export interface CatalogProduct {
  id:            string;
  company_id:    string;
  sku:           string;
  name:          string;
  price:         number;
  image_url:     string | null;
  group_name:    string | null;
  /** legal_name of the supplying company — shown in multi-vendor catalogs. */
  supplier_name: string | null;
  /** Phase 64: current stock balance (SUM of movements). 0 when no data. */
  available_qty: number;
  /**
   * Phase 64: COALESCE(product.b2b_backorder_override, company.allow_b2b_backorders).
   * true  → ordering allowed even when stock ≤ 0.
   * false → ordering blocked when stock ≤ 0.
   */
  effective_backorder_allowed: boolean;
  description?: string | null;
}

export interface CartItem {
  product:  CatalogProduct;
  quantity: number;
}

interface CartState {
  items: CartItem[];
}

type CartAction =
  | { type: 'ADD_ITEM';     product: CatalogProduct; quantity: number }
  | { type: 'SET_QUANTITY'; productId: string;       quantity: number }
  | { type: 'REMOVE_ITEM';  productId: string }
  | { type: 'CLEAR' };

// ─── Reducer ─────────────────────────────────────────────────────────────────

function cartReducer(state: CartState, action: CartAction): CartState {
  switch (action.type) {
    case 'ADD_ITEM': {
      const existing = state.items.find(i => i.product.id === action.product.id);
      if (existing) {
        return {
          items: state.items.map(i =>
            i.product.id === action.product.id
              ? { ...i, quantity: i.quantity + action.quantity }
              : i,
          ),
        };
      }
      return { items: [...state.items, { product: action.product, quantity: action.quantity }] };
    }
    case 'SET_QUANTITY': {
      if (action.quantity <= 0) {
        return { items: state.items.filter(i => i.product.id !== action.productId) };
      }
      return {
        items: state.items.map(i =>
          i.product.id === action.productId ? { ...i, quantity: action.quantity } : i,
        ),
      };
    }
    case 'REMOVE_ITEM':
      return { items: state.items.filter(i => i.product.id !== action.productId) };
    case 'CLEAR':
      return { items: [] };
    default:
      return state;
  }
}

// ─── Context ─────────────────────────────────────────────────────────────────

interface CartContextType {
  items:        CartItem[];
  totalItems:   number;   // sum of quantities
  uniqueItems:  number;   // count of distinct products
  totalPrice:   number;
  addItem:      (product: CatalogProduct, quantity: number) => void;
  setQuantity:  (productId: string, quantity: number) => void;
  removeItem:   (productId: string) => void;
  clearCart:    () => void;
  getQuantity:  (productId: string) => number;
}

const CartContext = createContext<CartContextType | undefined>(undefined);

// ─── Provider ─────────────────────────────────────────────────────────────────

export function B2BCartProvider({ children }: { children: ReactNode }) {
  const [state, dispatch] = useReducer(cartReducer, { items: [] });

  const addItem     = useCallback((product: CatalogProduct, quantity: number) =>
    dispatch({ type: 'ADD_ITEM', product, quantity }), []);

  const setQuantity = useCallback((productId: string, quantity: number) =>
    dispatch({ type: 'SET_QUANTITY', productId, quantity }), []);

  const removeItem  = useCallback((productId: string) =>
    dispatch({ type: 'REMOVE_ITEM', productId }), []);

  const clearCart   = useCallback(() =>
    dispatch({ type: 'CLEAR' }), []);

  const getQuantity = useCallback((productId: string): number =>
    state.items.find(i => i.product.id === productId)?.quantity ?? 0, [state.items]);

  const totalItems  = state.items.reduce((s, i) => s + i.quantity, 0);
  const uniqueItems = state.items.length;
  const totalPrice  = state.items.reduce((s, i) => s + i.product.price * i.quantity, 0);

  return (
    <CartContext.Provider value={{
      items: state.items,
      totalItems,
      uniqueItems,
      totalPrice,
      addItem,
      setQuantity,
      removeItem,
      clearCart,
      getQuantity,
    }}>
      {children}
    </CartContext.Provider>
  );
}

// ─── Hook ─────────────────────────────────────────────────────────────────────

export function useB2BCart() {
  const ctx = useContext(CartContext);
  if (!ctx) throw new Error('useB2BCart must be used inside B2BCartProvider');
  return ctx;
}
